/**
 * Shared pieces of the style rules ST-01 ... ST-09 (W2-12a checker-runtime; DESIGN.md §7.6), used by the rule
 * file (src/features/checker/rules/style.rules.ts, which runs in the analysis worker) and by the fix commands that
 * run on the main thread (src/features/checker/fixes.ts):
 *  - the `problems.fix.*` command ids the style rules reference beyond the ones of core/check/geometry.ts
 *    (`STYLE_FIX_COMMANDS`, argument shapes `StyleFixArgs`), and `workspaceFix` for the contracted
 *    `app.workspace` navigation command;
 *  - the RPE "Other" thresholds and the division tags Phira's upload form uses;
 *  - `redundantLinearKeys`: interior keys of a run of straight segments that lie on the straight line between
 *    their neighbours (in SECONDS, as Phira evaluates progress), so deleting them leaves the motion unchanged.
 *    The rule offers the deletion; the fix re-checks the keys against the document it runs on.
 */

import { LINEAR } from '../ease/index';
import type { ChartDoc, DocIndexes, Key, KeyId, LineProp, TrackId } from '../model/types';
import type { TempoMap } from '../time/types';
import type { MessageRef } from '../util/types';
import type { FixRef } from './types';

// ---------------------------------------------------------------------------------------------------------------
// Fix commands
// ---------------------------------------------------------------------------------------------------------------

/** Document-changing fixes of the style rules (registered by src/features/checker/fixes.ts). */
export const STYLE_FIX_COMMANDS = [
  'problems.fix.confirmTiming',
  'problems.fix.removeAssets',
  'problems.fix.deleteLines',
  'problems.fix.removeRedundantKeys',
  'problems.fix.addTag',
] as const;

export type StyleFixCommandId = (typeof STYLE_FIX_COMMANDS)[number];

export interface StyleFixArgs {
  /** Core.tempo.setTimingConfirmed { confirmed: true }. ST-01. */
  readonly 'problems.fix.confirmTiming': Readonly<Record<string, never>>;
  /** Core.doc.removeAsset { references: 'fail' } for each asset still unused. ST-03. */
  readonly 'problems.fix.removeAssets': { readonly assets: readonly string[] };
  /** Core.lines.deleteLines { children: 'detach' } (lines that still exist). ST-04. */
  readonly 'problems.fix.deleteLines': { readonly lines: readonly number[] };
  /** Core.keys.deleteKeys on the keys that are still redundant on the current document. ST-05. */
  readonly 'problems.fix.removeRedundantKeys': { readonly keys: readonly number[] };
  /** Core.doc.patchMeta: adds the tag to meta.tags. ST-09. */
  readonly 'problems.fix.addTag': { readonly tag: string };
}

export function styleFixRef<K extends StyleFixCommandId>(command: K, label: MessageRef, args: StyleFixArgs[K]): FixRef {
  return { label, command, args };
}

/** Workspaces of the contracted `app.workspace` command (app/registry/commands.ts). */
export type WorkspaceArg = 'timing' | 'chart' | 'publish';

/** Navigation fix through `app.workspace` ("Open Song & Timing", "Open Publish"). */
export function workspaceFix(label: MessageRef, workspace: WorkspaceArg): FixRef {
  return { label, command: 'app.workspace', args: { workspace } };
}

// ---------------------------------------------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------------------------------------------

/** Tags of which Phira's upload form asks for exactly one (the chart's division). */
export const DIVISION_TAGS: readonly string[] = Object.freeze(['regular', 'troll', 'plain', 'visual']);

/** RPE's placeholder for unknown chart details. */
export const UNKNOWN_DETAIL = 'UK';

/** A run of at least this many straight segments in a row is reported (ST-05). */
export const LINEAR_RUN_MIN_SEGMENTS = 16;

/** Properties ST-05 looks at (RPE's "Too Many Linear" check covers position and rotation; we add the others). */
export const LINEAR_RUN_PROPS: readonly LineProp[] = Object.freeze([
  'moveX',
  'moveY',
  'rotate',
  'alpha',
  'scaleX',
  'scaleY',
  'speed',
]);

/** Relative tolerance of the collinearity test (values are f64 in the model; Phira evaluates in f32). */
const COLLINEAR_EPS = 1e-6;

// ---------------------------------------------------------------------------------------------------------------
// Straight segments
// ---------------------------------------------------------------------------------------------------------------

/** True for the whole linear curve (RPE easing 1 without a slice). */
export function isLinearEase(k: Pick<Key, 'ease'>): boolean {
  const e = k.ease;
  return (
    e.kind === 'tween' &&
    LINEAR.kind === 'tween' &&
    e.id === LINEAR.id &&
    e.left === LINEAR.left &&
    e.right === LINEAR.right
  );
}

/** A maximal run of keys joined by straight segments without jumps or link groups. */
export interface LinearRun {
  /** Keys of the run in time order (segments = keys.length - 1). */
  readonly keys: readonly Key[];
}

function numeric(k: Key): boolean {
  return typeof k.vIn === 'number' && typeof k.vOut === 'number';
}

/**
 * Runs of consecutive straight segments of a key list (sorted by time): a segment counts when its start key's
 * easing is linear, its start key is not part of a link group and the key it arrives at has no jump. Only runs of at
 * least `minSegments` segments are returned.
 */
export function linearRuns(keys: readonly Key[], minSegments: number): LinearRun[] {
  const runs: LinearRun[] = [];
  let start = 0;
  const flush = (end: number): void => {
    if (end - start >= minSegments) runs.push({ keys: keys.slice(start, end + 1) });
  };
  for (let i = 0; i < keys.length - 1; i++) {
    const a = keys[i] as Key;
    const b = keys[i + 1] as Key;
    const straight = isLinearEase(a) && a.link === 0 && numeric(a) && numeric(b) && b.vIn === b.vOut;
    if (!straight) {
      flush(i);
      start = i + 1;
    }
  }
  flush(keys.length - 1);
  return runs;
}

function near(a: number, b: number): boolean {
  return Math.abs(a - b) <= COLLINEAR_EPS * Math.max(1, Math.abs(a), Math.abs(b));
}

/**
 * Interior keys of a straight run that can be deleted without changing the motion: each lies on the straight
 * line (value over seconds) from the last key kept to the next key. Deleting a key merges its two segments into one
 * straight segment from the kept key, so after the deletions every remaining segment still passes through the
 * removed points.
 */
export function redundantLinearKeys(run: readonly Key[], tempo: Pick<TempoMap, 'secAt'>): KeyId[] {
  const out: KeyId[] = [];
  if (run.length < 3) return out;
  let anchor = run[0] as Key;
  for (let i = 1; i < run.length - 1; i++) {
    const k = run[i] as Key;
    const next = run[i + 1] as Key;
    const t0 = tempo.secAt(anchor.t);
    const t1 = tempo.secAt(k.t);
    const t2 = tempo.secAt(next.t);
    const v0 = anchor.vOut as number;
    const v1 = k.vIn as number;
    const v2 = next.vIn as number;
    const span = t2 - t0;
    const onLine = span > 0 && near(v1, v0 + ((v2 - v0) * (t1 - t0)) / span);
    if (onLine) out.push(k.id);
    else anchor = k;
  }
  return out;
}

/** Keys of a track in time order. */
export function keysOfTrack(doc: Pick<ChartDoc, 'keys'>, idx: Pick<DocIndexes, 'keysByTrack'>, track: TrackId): Key[] {
  const out: Key[] = [];
  for (const id of idx.keysByTrack(track)) {
    const k = doc.keys.get(id);
    if (k !== undefined) out.push(k);
  }
  return out;
}
